<script setup lang="ts">
const props = defineProps({
  collectible: {
    type: Object as PropType<Collectible>,
    required: true,
  },
});
const { trackEvent } = useGAnalytics();
const { hideRecommendedPrice } = useRuntimeConfig().public;
const route = useRoute();
const userStore = useUserStore();
const isHovered = ref<boolean>(false);
const isMobile = useMediaQuery("(max-width: 767px)");
const isStacked = computed(
  () => !!(props.collectible.total && props.collectible.total > 1)
);

const collectibleDetailUrl = computed(() => {
  return `/market/${route.params.merchant}/${props.collectible.id}`;
});

const stackListUrl = computed(() => {
  const baseUrl = `/market/${route.params.merchant}/stack/${props.collectible.setId}`;
  const params = new URLSearchParams();

  if (route.query.filter) {
    params.append("filter", route.query.filter.toString());
  }
  if (route.query.listed) {
    params.append("listed", route.query.listed.toString());
  }

  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
});

const sendTrackEvent = (isStacked: boolean) => {
  const eventName = isStacked
    ? "view_collectible_stack"
    : `click_collectible_market`;

  trackEvent(eventName, {
    collectibleId: isStacked ? props.collectible.setId : props.collectible.id,
  });
};

const dataTestText = (isStacked: boolean) => {
  return isStacked
    ? `link-market-collectible-stack-${props.collectible.setId}`
    : `link-market-collectible-${props.collectible.id}`;
};

const { getMerchantAttributes } = useLuxuryDesign();
const collectibleDesign = computed(() =>
  getMerchantAttributes(props.collectible.merchantId)
);
const isLuxuryDesign =
  collectibleDesign.value["data-merchant-design"] === "luxury";
</script>

<template>
  <div>
    <div
      v-bind="collectibleDesign"
      class="relative grid grid-rows-[1fr,1fr,1fr,1fr, 1fr] grid-cols-1 border rounded-lg border-primary-900 will-change-transform transition ease-in-out duration-300 overflow-visible collectible-card"
      :class="{ '-translate-y-1': isHovered }"
      @mouseover="isHovered = true"
      @mouseleave="isHovered = false">
      <NuxtLinkLocale
        :to="isStacked ? stackListUrl : collectibleDetailUrl"
        @click="sendTrackEvent(isStacked)"
        :data-test="dataTestText(isStacked)">
        <div class="relative overflow-hidden">
          <CardImage :src="getPortraitItemImage(collectible.imageId)" />
        </div>
        <div
          v-if="collectible.id !== 'default-jewel' && !isLuxuryDesign"
          class="absolute w-full grid grid-rows-[4px_2px] z-10 collectible-card__rarity">
          <div :class="getRarityColor(collectible.rarity!, 'bg')"></div>
          <div
            class="opacity-80"
            :class="getRarityColor(collectible.rarity!, 'bg')"></div>
        </div>
        <div
          class="px-2 py-3.5 border-t border-primary-900 flex justify-center truncate merchant-card__surface">
          <h1
            class="text-white font-bold whitespace-nowrap truncate merchant-heading"
            :class="{
              'merchant-heading--highlight': collectible.id === 'default-jewel',
            }">
            {{ $t(`${collectible.translationId}.title`) }}
            {{ $t(`${collectible.translationId}.subtitle`) }}
          </h1>
        </div>
        <div
          v-if="
            collectible.price?.GFAL ||
            hideRecommendedPrice.toString() !== 'true'
          "
          class="relative flex flex-col gap-1 border-t border-primary-900 shadow-xs focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 merchant-card__surface">
          <div class="px-2 py-2 flex justify-between">
            <template v-if="collectible.id === 'default-jewel'">
              <div class="flex items-center justify-center gap-2 w-full">
                <BaseIcon name="GFAL_token" class="w-4" />
                <p class="text-neutral-200 font-semibold">Coming Soon</p>
              </div>
            </template>
            <template
              v-else-if="
                collectible.price?.GFAL ||
                collectible.recommendedPrice.GFAL !== null ||
                collectible.recommendedPrice.GFAL !== undefined
              ">
              <p
                v-if="isStacked"
                class="text-secondary-200 font-semibold text-xs pt-1">
                {{ $t("main.cardCollectible.starting") }}
              </p>
              <p v-else class="text-secondary-200 font-semibold text-xs pt-1">
                {{
                  collectible.price?.GFAL
                    ? $t("main.cardCollectible.buyNow")
                    : $t("main.cardCollectible.recommendedPrice")
                }}
              </p>
              <div class="flex flex-col items-end">
                <div class="flex gap-2">
                  <BaseIcon name="GFAL_token" class="w-4" />
                  <p
                    v-if="
                      collectible.price?.GFAL ||
                      collectible.recommendedPrice.GFAL !== null ||
                      collectible.recommendedPrice.GFAL !== undefined
                    "
                    class="text-neutral-200 font-bold">
                    {{
                      roundNum(
                        collectible.price?.GFAL ||
                          collectible.recommendedPrice.GFAL
                      )
                    }}
                  </p>
                </div>
                <p
                  v-if="
                    collectible.price?.USD ||
                    collectible.recommendedPrice.USD !== null ||
                    collectible.recommendedPrice.USD !== undefined
                  "
                  class="text-neutral-300 text-xs font-semibold">
                  {{
                    "$" +
                    roundNum(
                      collectible.price?.USD || collectible.recommendedPrice.USD
                    )
                  }}
                </p>
              </div>
            </template>
          </div>
        </div>
        <div
          v-if="
            (collectible.number != null &&
              collectible.number !== undefined) ||
            (collectible.level && collectible.level > 0)
          "
          class="border-t border-primary-900 px-2 py-2 flex items-center gap-4 text-[10px] font-semibold merchant-card__surface">
          <div
            v-if="collectible.number != null && collectible.number !== undefined"
            class="relative flex gap-1"
            :class="isMobile ? 'flex-col items-start gap-0.5' : 'items-center'">
            <template v-if="isMobile">
              <span class="text-neutral-200">{{ collectible.number }}</span>
              <span class="text-[10px] text-neutral-400 leading-none">
                {{ $t("main.cardDetail.tooltip.mintNumberTitle") }}
              </span>
            </template>
            <template v-else>
              <span class="text-neutral-400">
                {{ $t("main.cardDetail.tooltip.mintNumberTitle") }}
              </span>
              <span class="peer text-neutral-200 cursor-help">
                #{{ collectible.number }}
              </span>
              <CommonTooltip position="top">
                <template #tooltip-text>
                  <div class="max-w-xs text-xs text-neutral-800 leading-relaxed">
                    <p class="font-semibold mb-1">
                      {{ $t("main.cardDetail.tooltip.mintNumberTitle") }}
                    </p>
                    <p>
                      {{ $t("main.cardDetail.tooltip.mintNumber") }}
                    </p>
                  </div>
                </template>
              </CommonTooltip>
            </template>
          </div>
          <div
            v-if="collectible.level && collectible.level > 0"
            class="relative flex gap-1"
            :class="isMobile ? 'flex-col items-start gap-0.5' : 'items-center'">
            <!-- <BaseIconHexagon class="w-4 h-4 text-neutral-200" /> -->
            <template v-if="isMobile">
              <span class="text-neutral-200">{{ collectible.level }}</span>
              <span class="text-[10px] text-neutral-400 leading-none">
                {{ $t("main.cardDetail.tooltip.itemValueTitle") }}
              </span>
            </template>
            <template v-else>
              <span class="text-neutral-400">
                {{ $t("main.cardDetail.tooltip.itemValueTitle") }}
              </span>
              <span class="peer text-neutral-200 cursor-help">
                {{ collectible.level }}
              </span>
              <CommonTooltip position="top">
                <template #tooltip-text>
                  <div class="max-w-xs text-xs text-neutral-800 leading-relaxed">
                    <p class="font-semibold mb-1">
                      {{ $t("main.cardDetail.tooltip.itemValueTitle") }}
                    </p>
                    <p>
                      {{ $t("main.cardDetail.tooltip.itemValue") }}
                    </p>
                  </div>
                </template>
              </CommonTooltip>
            </template>
          </div>
        </div>

        <div
          v-if="isStacked"
          class="grow px-2 py-2 border-t border-primary-900 rounded-b-lg flex justify-center items-center gap-4 merchant-card__surface">
          <p class="text-neutral-300 text-xs font-bold">
            {{ collectible.total?.toString() }}
            {{ $t("main.cardCollectible.amountAvailable") }}
          </p>
        </div>
      </NuxtLinkLocale>

      <div
        v-if="!isStacked"
        class="grow px-2 py-2 border-t border-primary-900 rounded-b-lg flex justify-center items-center gap-3 group merchant-card__surface">
        <CommonUserCard
          :id="collectible.gfalId"
          size="custom"
          custom-class="w-9"
          :special-border="userStore.isCurrentUserId(collectible.gfalId)"
          font-size="text-sm"
          :special-text="userStore.isCurrentUserId(collectible.gfalId)" />
      </div>
    </div>
    <CardStack v-if="isStacked" v-model:is-hovered="isHovered" />
  </div>
</template>
